import { apiFetch } from './api';
import type { PteroEnvId } from './pterodactyl-config.service';

export type StepType = 'command' | 'power' | 'sleep' | 'update-images' | 'update-apis' | 'cdn-promote';
export type PowerSignal = 'start' | 'stop' | 'restart' | 'kill';

export interface ProcedureStep {
  type: StepType;
  label?: string;
  imageFilter?: string;     // pour command/power/update-images/update-apis
  command?: string;         // pour command
  powerSignal?: PowerSignal; // pour power
  sleepSeconds?: number;    // pour sleep
  sourceEnv?: string;       // pour cdn-promote
  /** Pour update-apis : true (défaut) = ne restart QUE les APIs bumpées.
   *  false = restart TOUTES les APIs matchées, même déjà à jour. */
  restartOnlyIfUpdated?: boolean;
}

export interface UpdateProcedure {
  steps: ProcedureStep[];
}

export type ProceduresByEnv = Partial<Record<PteroEnvId, UpdateProcedure>>;

export type ProceduresByName = Record<string, UpdateProcedure>;

export const procedureService = {
  // === Legacy : 1 procédure par env (la "Défaut") ===
  listAll: (projectId: string) =>
    apiFetch<ProceduresByEnv>(`/api/projects/${projectId}/procedure`),
  get: (projectId: string, envId: PteroEnvId) =>
    apiFetch<UpdateProcedure>(`/api/projects/${projectId}/procedure/${envId}`),
  update: (projectId: string, envId: PteroEnvId, proc: UpdateProcedure) =>
    apiFetch<UpdateProcedure>(`/api/projects/${projectId}/procedure/${envId}`, {
      method: 'PUT', json: proc,
    }),
  /** Récupère la procédure par défaut Paladium (sans modifier le projet). */
  getDefault: (projectId: string) =>
    apiFetch<UpdateProcedure>(`/api/projects/${projectId}/procedure-default`),

  // === Multi-procédures nommées par env ===
  /** Liste toutes les procédures nommées de l'env (Map<nom, procédure>). */
  listByName: (projectId: string, envId: PteroEnvId) =>
    apiFetch<ProceduresByName>(`/api/projects/${projectId}/procedures/${envId}`),
  /** Récupère une procédure nommée précise. */
  getByName: (projectId: string, envId: PteroEnvId, procName: string) =>
    apiFetch<UpdateProcedure>(`/api/projects/${projectId}/procedures/${envId}/${encodeURIComponent(procName)}`),
  /** Sauvegarde (create ou update) une procédure nommée. */
  saveByName: (projectId: string, envId: PteroEnvId, procName: string, proc: UpdateProcedure) =>
    apiFetch<UpdateProcedure>(`/api/projects/${projectId}/procedures/${envId}/${encodeURIComponent(procName)}`, {
      method: 'PUT', json: proc,
    }),
  /** Supprime une procédure nommée. Refuse si c'est la dernière. */
  deleteByName: (projectId: string, envId: PteroEnvId, procName: string) =>
    apiFetch<void>(`/api/projects/${projectId}/procedures/${envId}/${encodeURIComponent(procName)}`, {
      method: 'DELETE',
    }),
};

// Présets affichables côté UI
export const STEP_TYPE_LABELS: Record<StepType, string> = {
  'command':       'Commande console',
  'power':         'Signal power (start/stop/...)',
  'sleep':         'Pause (attente)',
  'update-images': 'Update images docker (containers MC)',
  'update-apis':   'Update APIs (bump version + restart)',
  'cdn-promote':   'Push RPs sur CDN cible',
};

export const POWER_SIGNAL_LABELS: Record<PowerSignal, string> = {
  start: 'Start (démarrer)',
  stop: 'Stop (arrêt propre)',
  restart: 'Restart',
  kill: 'Kill (force)',
};
